import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Layout } from '@/components/layout/Layout'
import { LoginPage } from '@/pages/auth/LoginPage'
import DashboardPage from '@/components/pages/dashboard'
import UploadPODPage from '@/components/pages/uploadPOD'
import BulkPodUploadPage from '@/components/pages/bulkPodUpload'
import ApprovalsSyncPage from '@/components/pages/itAdmin/ApprovalsSyncPage'
import PendingPODPage from '@/components/pages/pendingPOD'
import PendingPODReportsPage from '@/components/pages/pendingPODReports'
import PODReportsPage from '@/components/pages/reportsPOD'
import DocumentJourneyPage from '@/components/pages/documentJourney'
import DocumentJourneyDetailPage from '@/components/pages/documentJourney/DetailPage'
import ApprovalDetailPage from '@/components/pages/approvalDetail'
import LoadingSpinner from '@/components/CustomComponents/LoadingSpinner'
import TransporterPendingUploadPage from '@/components/pages/transporterPendingUpload'
import { defaultAuthedPath, isPodViewer } from '@/utils/authPaths'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  if (isAuthenticated) return <Navigate to={defaultAuthedPath(user)} replace />
  return <>{children}</>
}

function RequirePermissions({
  required,
  children,
}: {
  required: string[] | string
  children: React.ReactNode
}) {
  const { user } = useAuthStore()
  const req = Array.isArray(required) ? required : [required]
  const perms = Array.isArray(user?.permissions) ? user?.permissions : []
  const isViewer = isPodViewer(user)
  const ok = isViewer || req.every((p) => perms.includes(p))
  if (!ok) return <Navigate to={defaultAuthedPath(user)} replace />
  return <>{children}</>
}

function RequireRole({ role, children }: { role: string; children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (String(user?.pod_role || '') !== role) return <Navigate to={defaultAuthedPath(user)} replace />
  return <>{children}</>
}

function HomeRedirect() {
  const { user } = useAuthStore()
  return <Navigate to={defaultAuthedPath(user)} replace />
}

function AppRoutes() {
  const { isLoading, isAuthenticated, validateSession } = useAuthStore()

  useEffect(() => {
    validateSession()
  }, [validateSession])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRedirect />} />

        <Route path="dashboard" element={<DashboardPage />} />

        <Route
          path="uploadPOD"
          element={
            <RequirePermissions required="pod.upload">
              <UploadPODPage />
            </RequirePermissions>
          }
        />
        <Route
          path="bulkPodUpload"
          element={
            <RequirePermissions required="pod.bulk_upload">
              <BulkPodUploadPage />
            </RequirePermissions>
          }
        />
        <Route
          path="it-admin/approvals-sync"
          element={
            <RequirePermissions required="pod.sync_approvals">
              <ApprovalsSyncPage />
            </RequirePermissions>
          }
        />
        <Route
          path="pendingPOD"
          element={
            <RequirePermissions required="pod.approve">
              <PendingPODPage />
            </RequirePermissions>
          }
        />
        <Route path="pendingPODReports" element={<PendingPODReportsPage />} />
        <Route path="PODReports" element={<PODReportsPage />} />
        <Route path="documentJourney" element={<DocumentJourneyPage />} />
        <Route path="documentJourney/:documentNo" element={<DocumentJourneyDetailPage />} />
        <Route path="approvals/:id" element={<ApprovalDetailPage />} />

        <Route
          path="transporter/dashboard"
          element={
            <RequireRole role="POD Transporter">
              <DashboardPage />
            </RequireRole>
          }
        />
        <Route
          path="transporter/pending-upload"
          element={
            <RequireRole role="POD Transporter">
              <TransporterPendingUploadPage />
            </RequireRole>
          }
        />
        <Route
          path="transporter/document-journey"
          element={
            <RequireRole role="POD Transporter">
              <DocumentJourneyPage transporterScoped />
            </RequireRole>
          }
        />
        <Route
          path="transporter/document-journey/:documentNo"
          element={
            <RequireRole role="POD Transporter">
              <DocumentJourneyDetailPage transporterScoped />
            </RequireRole>
          }
        />
        <Route
          path="transporter/pending-approve"
          element={
            <RequireRole role="POD Transporter">
              <PendingPODReportsPage
                initialStatus="Pending"
                fixedTransporterCode
                pageTitle="Pending approval"
                pageDescription="POD you submitted that are awaiting approval from POD Admin"
              />
            </RequireRole>
          }
        />
        <Route
          path="transporter/approved"
          element={
            <RequireRole role="POD Transporter">
              <PendingPODReportsPage
                initialStatus="Approved"
                fixedTransporterCode
                pageTitle="Transporter reports"
                pageDescription="Approved POD for your transporter code"
              />
            </RequireRole>
          }
        />
      </Route>
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  )
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <AppRoutes />
    </div>
  )
}

export default App
